import React, { useEffect, useState } from "react";
import { Card, Descriptions, Tag, Table, Divider, Button, Skeleton } from "antd";
import { ArrowLeftOutlined, DownloadOutlined } from "@ant-design/icons";
import logo from "../images/logo.png";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useParams } from "react-router-dom";
import axios from "axios";
import SideBar from "../component/SideBar";

const OrderDetail = () => {
    const { id } = useParams();
    const [ordDetail, setOrdDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    const getOrdDetails = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}api/view-order?order_id=${id}`
            );
            setOrdDetail(res.data);
        } catch (error) {
            console.log("Order Detail Fetching Content", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getOrdDetails();
    }, [id]);

    const companyLogo = logo;
    const company = {
        name: "Blue Water Fragrances",
        address: "123 Avenue Wilson Street, Springfield, WY",
        email: "info@bluewaterfragrances.com",
        phone: "(774) 268-2232",
    };

    const handleDownload = async () => {
        if (!ordDetail) return;

        const { jsPDF } = await import("jspdf");
        const { default: autoTable } = await import("jspdf-autotable");

        const order = ordDetail.order;
        const products = ordDetail.order_items || [];
        const doc = new jsPDF({ unit: "pt", format: "a4" });

        const pageWidth = doc.internal.pageSize.getWidth();

        const logoImg = new Image();
        logoImg.src = companyLogo;

        logoImg.onload = async () => {
            const logoWidth = 70;
            const logoHeight = 40;
            const marginX = 40;

            doc.addImage(logoImg, "PNG", marginX, 40, logoWidth, logoHeight);

            doc.setFontSize(14);
            doc.text(company.name, pageWidth / 2, 50, { align: "center" });
            doc.setFontSize(10);
            doc.text(company.address, pageWidth / 2, 65, { align: "center" });
            doc.text(`Email: ${company.email}`, pageWidth / 2, 80, { align: "center" });
            doc.text(`Phone: ${company.phone}`, pageWidth / 2, 95, { align: "center" });

            doc.setFontSize(18);
            doc.setTextColor(0, 102, 204);
            doc.text("INVOICE", pageWidth - marginX, 50, { align: "right" });

            doc.setTextColor(0);
            doc.setFontSize(11);
            doc.text(`Order ID: ${order.order_number}`, pageWidth - marginX, 70, { align: "right" });
            doc.text(`Date: ${new Date(order.created_at).toLocaleDateString()}`, pageWidth - marginX, 85, { align: "right" });
            doc.text(`Status: ${order.order_status}`, pageWidth - marginX, 100, { align: "right" });

            doc.setFontSize(13);
            doc.text("Billing Details", marginX, 130);
            doc.setFontSize(10);
            doc.text(`Customer: ${order.billing_first_name} ${order.billing_last_name}`, marginX, 145);
            doc.text(`Email: ${order.billing_email}`, marginX, 160);
            doc.text(`Phone: ${order.billing_contact}`, marginX, 175);
            doc.text(`Shipping: ${order.billing_address1}, ${order.billing_state}`, marginX, 190);

            autoTable(doc, {
                startY: 220,
                head: [["Product", "Qty", "Price", "Subtotal"]],
                body: products.map((p) => [
                    p.name,
                    p.quantity,
                    `$${p.price}`,
                    `$${(p.price * p.quantity).toFixed(2)}`
                ]),
                margin: { left: marginX, right: marginX },
                tableWidth: pageWidth - marginX * 2,
                headStyles: {
                    fillColor: [0, 102, 204],
                    textColor: 255,
                    halign: "center",
                },
                styles: {
                    fontSize: 10,
                    cellPadding: 6,
                    valign: "middle",
                    overflow: "linebreak",
                },
                columnStyles: {
                    0: { halign: "left", minCellWidth: 220, cellWidth: 220 },
                    1: { halign: "center", minCellWidth: 50 },
                    2: { halign: "center", minCellWidth: 60 },
                    3: { halign: "center", minCellWidth: 60 },
                },
                didParseCell: function (data) {
                    if (data.section === 'head' && data.column.index === 0) {
                        data.cell.styles.halign = 'left';
                    }
                },
            });
            const finalY = doc.lastAutoTable.finalY + 30;
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text(`Grand Total: $${order.total}`, pageWidth - marginX, finalY, { align: "right" });
            doc.save(`Invoice_${order.order_number}.pdf`);
        };

        logoImg.onerror = () => console.error("Logo failed to load.");
    };



    const products = ordDetail?.order_items || [];
    const order = ordDetail?.order || {};

    return (
        <div className="flex min-h-screen bg-gray-100 pt-[160px] pb-[50px]">
            <div className="w-64 bg-brandBlue shadow-lg py-8 px-4 text-white rounded-tr-[10px] rounded-br-[10px]">
                <h2 className="text-2xl font-bold mb-10">My Account</h2>
                <SideBar />
            </div>
            <div className="flex-1 p-10 pt-0 pb-0">
                <Card className="shadow-xl rounded-2xl p-6 bg-white">
                    <div className="flex justify-between items-center mb-6">
                        <Button
                            type="link"
                            icon={<ArrowLeftOutlined />}
                            className="text-brandBlue"
                            onClick={() => window.history.back()}
                        >
                            Back to Orders
                        </Button>
                        <div className="flex items-center gap-3">
                            <h2 className="text-xl font-bold text-brandBlue">Invoice</h2>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                className="bg-brandBlue border-none"
                                onClick={handleDownload}
                            >
                                Download Invoice
                            </Button>
                        </div>
                    </div>
                    {loading ? (
                        <div className="space-y-8 animate-pulse">
                            <div className="flex justify-between items-center">
                                <div className="w-32 h-6 bg-gray-200 rounded-md"></div>
                                <div className="flex gap-3">
                                    <div className="w-24 h-8 bg-gray-200 rounded-md"></div>
                                    <div className="w-36 h-8 bg-gray-200 rounded-md"></div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="h-6 bg-gray-200 rounded-md"></div>
                                <div className="h-6 bg-gray-200 rounded-md"></div>
                                <div className="h-6 bg-gray-200 rounded-md"></div>
                                <div className="h-6 bg-gray-200 rounded-md"></div>
                            </div>
                            <div>
                                <div className="w-48 h-5 bg-gray-300 mb-3 rounded-md"></div>
                                <div className="space-y-2">
                                    <div className="h-5 bg-gray-200 rounded-md w-3/4"></div>
                                    <div className="h-5 bg-gray-200 rounded-md w-2/3"></div>
                                    <div className="h-5 bg-gray-200 rounded-md w-1/2"></div>
                                    <div className="h-5 bg-gray-200 rounded-md w-5/6"></div>
                                </div>
                            </div>
                            <div className="mt-6 space-y-2">
                                {[...Array(4)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="grid grid-cols-4 gap-3 items-center bg-gray-100 rounded-md p-2"
                                    >
                                        <div className="h-5 bg-gray-200 rounded-md w-3/4"></div>
                                        <div className="h-5 bg-gray-200 rounded-md w-1/2"></div>
                                        <div className="h-5 bg-gray-200 rounded-md w-1/3"></div>
                                        <div className="h-5 bg-gray-200 rounded-md w-1/4"></div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end mt-6">
                                <div className="w-32 h-6 bg-gray-200 rounded-md"></div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <Descriptions bordered column={2} size="middle" className="mb-6">
                                <Descriptions.Item label="Order ID">{order.order_number}</Descriptions.Item>
                                <Descriptions.Item label="Date">
                                    {new Date(order.created_at).toLocaleDateString()}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag
                                        color={
                                            order.order_status === "Delivered" || order.order_status === "confirmed" || order.order_status === "created"
                                                ? "green"
                                                : order.order_status === "pending" || order.order_status === "Pending"
                                                    ? "gold"
                                                    : "red"
                                        }
                                    >
                                        {order.order_status?.toUpperCase()}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Payment">{order.payment_status}</Descriptions.Item>
                                <Descriptions.Item label="Total" span={2}>
                                    <span className="font-bold text-brandBlue">${order.total}</span>
                                </Descriptions.Item>
                            </Descriptions>
                            <Divider>Billing & Shipping</Divider>
                            <Descriptions bordered column={1} size="middle" className="mb-6">
                                <Descriptions.Item label="Customer">
                                    {order.billing_first_name} {order.billing_last_name}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">{order.billing_email}</Descriptions.Item>
                                <Descriptions.Item label="Phone">{order.billing_contact}</Descriptions.Item>
                                <Descriptions.Item label="Shipping Address">
                                    {order.billing_address1}, {order.billing_address2}, {order.billing_city},{" "}
                                    {order.billing_state}, {order.billing_country}
                                </Descriptions.Item>
                            </Descriptions>
                            <Divider>Products</Divider>
                            <Table
                                columns={[
                                    {
                                        title: "Product",
                                        dataIndex: "name",
                                        key: "name",
                                        render: (text, record) => (
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={`${process.env.REACT_APP_SITE_IMAGES}${record.image}`}
                                                    alt={record.name}
                                                    className="w-[50px] h-[50px] object-contain rounded-md border border-brandBlue-200 p-1"
                                                />
                                                <span className="font-medium text-gray-800">{text}</span>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: "Quantity",
                                        dataIndex: "quantity",
                                        key: "quantity",
                                        align: "center",
                                    },
                                    {
                                        title: "Price",
                                        dataIndex: "price",
                                        key: "price",
                                        align: "center",
                                        render: (price) => `$${price}`,
                                    },
                                    {
                                        title: "Subtotal",
                                        key: "subtotal",
                                        align: "center",
                                        render: (record) => `$${(record.price * record.quantity).toFixed(2)}`,
                                    },
                                ]}
                                dataSource={products}
                                pagination={{
                                    pageSize: 2,
                                    showSizeChanger: false,
                                    position: ["bottomCenter"],
                                }}
                                rowKey="id"
                                className="rounded-lg overflow-hidden"
                            />

                            <div className="flex justify-end mt-4">
                                <h2 className="text-lg font-bold">
                                    Grand Total:{" "}
                                    <span className="text-brandBlue">${order.total}</span>
                                </h2>
                            </div>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default OrderDetail;
